<template>
  <section id="contact" class="section contact">
    <div class="contact__intro">
      <p class="contact__eyebrow">Nächster Schritt</p>
      <h2>
        Wenn es um Softwarearchitektur, technische Leitung oder Vecura geht,
        lass uns sprechen.
      </h2>
      <p class="contact__intro-copy">
        Für konkrete Anfragen erreichst du mich direkt per E-Mail. LinkedIn
        eignet sich für beruflichen Kontext und Austausch. Alles rund um Vecura
        läuft über die Vecura-Website oder die Unternehmensmail.
      </p>
      <div class="contact__signature">
        <figure class="contact__portrait">
          <img
            :src="contactPortrait"
            alt="Portrait von Timo Rothdeutsch"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <div>
          <strong>Timo Rothdeutsch</strong>
          <span>Mitgründer & COO bei Vecura</span>
          <small>
            Softwarearchitektur, KI/RAG, Anforderungsanalyse und technische
            Führung zwischen Fachlichkeit, Produkt und Engineering.
          </small>
        </div>
      </div>
    </div>

    <div class="contact__body">
      <a
        v-for="channel in contactChannels"
        :key="channel.href"
        class="contact__channel"
        :href="channel.href"
        :target="isExternalLink(channel.href) ? '_blank' : undefined"
        :rel="isExternalLink(channel.href) ? 'noopener noreferrer' : undefined"
        :aria-label="`${channel.label}: ${channel.value}`"
      >
        <span class="contact__label" aria-hidden="true">
          <ContactChannelIcon
            class="contact__brand-icon"
            :type="getChannelIcon(channel.label)"
          />
        </span>
        <strong>{{ channel.value }}</strong>
        <em>{{ channel.note }}</em>
        <ExternalLinkIcon class="contact__target-icon" />
      </a>
    </div>
  </section>
</template>

<script setup lang="ts">
import ContactChannelIcon from "@/components/icons/ContactChannelIcon.vue";
import ExternalLinkIcon from "@/components/icons/ExternalLinkIcon.vue";
import contactPortrait from "@/assets/timo-contact-portrait.jpg";
import { contactChannels } from "@/data/profile";

function isExternalLink(href: string): boolean {
  return href.startsWith("http");
}

function getChannelIcon(label: string): "mail" | "linkedin" | "vecura" {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes("mail")) {
    return "mail";
  }

  if (normalizedLabel.includes("linkedin")) {
    return "linkedin";
  }

  return "vecura";
}
</script>

<style scoped>
.contact {
  display: grid;
  grid-template-columns: minmax(0, 0.84fr) minmax(360px, 1fr);
  gap: 56px;
  align-items: start;
  border-top: 1px solid var(--color-line);
  background: var(--color-paper-strong);
}

.contact__eyebrow {
  margin: 0 0 12px;
  color: var(--color-muted);
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  color: var(--color-ink);
  font-size: 2.25rem;
  line-height: 1.16;
}

.contact__intro-copy {
  margin: 22px 0 0;
  color: var(--color-muted);
  font-size: 1.04rem;
  line-height: 1.7;
}

.contact__body {
  display: grid;
  gap: 10px;
}

.contact__signature {
  display: inline-grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  width: min(100%, 360px);
  margin-top: 32px;
  padding-top: 18px;
  border-top: 1px solid var(--color-line);
}

.contact__signature strong,
.contact__signature span,
.contact__signature small {
  display: block;
}

.contact__signature strong {
  color: var(--color-ink);
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.2;
}

.contact__signature span {
  margin-top: 4px;
  color: var(--color-muted);
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.35;
}

.contact__signature small {
  margin-top: 8px;
  color: var(--color-muted);
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.42;
}

.contact__portrait {
  position: relative;
  overflow: hidden;
  width: 78px;
  aspect-ratio: 2 / 3;
  margin: 0;
  border: 1px solid var(--color-line);
  border-radius: 12px;
  background: var(--color-paper);
  box-shadow: 0 14px 34px rgba(31, 28, 20, 0.1);
}

.contact__portrait img {
  display: block;
  width: 100%;
  height: 100%;
  filter: grayscale(1) sepia(0.08) saturate(0.85) contrast(1.04);
  object-fit: cover;
  object-position: center top;
}

.contact__channel {
  position: relative;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 30px;
  gap: 6px 16px;
  align-items: center;
  min-height: 106px;
  border: 1px solid var(--color-line);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  padding: 15px 16px;
  color: inherit;
  text-decoration: none;
  transition: background 180ms ease, border-color 180ms ease,
    transform 180ms ease;
}

.contact__channel:hover {
  border-color: rgba(20, 20, 20, 0.24);
  background: rgba(255, 255, 255, 0.46);
  transform: translateY(-1px);
}

.contact__channel:hover strong,
.contact__channel:hover .contact__target-icon {
  color: var(--color-ink);
}

.contact__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-muted);
}

.contact__brand-icon {
  width: 22px;
  height: 22px;
  color: var(--color-muted);
  opacity: 0.58;
}

.contact__brand-icon :deep(img),
.contact__brand-icon :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}

.contact__brand-icon :deep(img) {
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.72;
}

.contact__channel strong {
  color: var(--color-ink-soft);
  font-size: 1rem;
  line-height: 1.3;
  overflow-wrap: anywhere;
  transition: color 180ms ease;
}

.contact__channel em {
  grid-column: 2;
  color: var(--color-muted);
  font-size: 0.95rem;
  font-style: normal;
  line-height: 1.55;
}

.contact__target-icon {
  grid-row: 1 / span 2;
  grid-column: 3;
  justify-self: end;
  width: 21px;
  height: 21px;
  color: var(--color-muted);
  transition: color 180ms ease, transform 180ms ease;
}

.contact__channel:hover .contact__target-icon {
  transform: translate(2px, -2px);
}

.contact__channel:hover .contact__brand-icon {
  opacity: 0.82;
}

.contact__channel:focus-visible {
  outline: 2px solid var(--color-yellow);
  outline-offset: 3px;
}

@media (max-width: 820px) {
  .contact {
    grid-template-columns: 1fr;
  }

  .contact__signature {
    display: none;
  }

  h2 {
    font-size: 1.7rem;
  }
}

@media (max-width: 620px) {
  .contact__channel {
    grid-template-columns: minmax(0, 1fr) 26px;
    min-height: auto;
  }

  .contact__label,
  .contact__channel strong,
  .contact__channel em {
    grid-column: 1;
  }

  .contact__target-icon {
    grid-row: 1 / span 3;
    grid-column: 2;
  }
}

@media (max-width: 760px) {
  .contact {
    grid-template-columns: 1fr;
    gap: 26px;
  }

  h2 {
    font-size: 1.75rem;
    line-height: 1.12;
  }

  .contact__intro-copy {
    margin-top: 16px;
    font-size: 0.98rem;
    line-height: 1.62;
  }

  .contact__body {
    gap: 9px;
  }

  .contact__channel {
    grid-template-columns: 32px minmax(0, 1fr) 24px;
    gap: 5px 12px;
    min-height: 86px;
    border-radius: 14px;
    padding: 14px;
  }

  .contact__label {
    grid-column: 1;
    grid-row: 1 / span 2;
  }

  .contact__brand-icon {
    width: 22px;
    height: 22px;
  }

  .contact__channel strong,
  .contact__channel em {
    grid-column: 2;
  }

  .contact__channel strong {
    font-size: 0.98rem;
  }

  .contact__channel em {
    font-size: 0.9rem;
    line-height: 1.45;
  }

  .contact__target-icon {
    grid-column: 3;
    grid-row: 1 / span 2;
    width: 19px;
    height: 19px;
  }
}
</style>
